import { useEffect, useRef } from 'react'

import {
  RankedToastFundedOfferQuery,
  useBostonFreeDeliveryTfoQuery,
  useRankedToastFundedOfferQuery
} from 'src/apollo/onlineOrdering'
import { useFormatMoney } from 'src/lib/js/hooks/useFormatPrice'
import { t } from 'src/translations'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useCart } from 'public/components/online_ordering/CartContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import { isToastAppProduct } from 'public/components/online_ordering/ToastProduct'

import {
  getAppliedFreeDeliveryAmount,
  getAppliedFundedOffersTotal,
  getNonFreeDeliveryFundedOffersTotal
} from './appliedFundedOffers'

// Derived from the query result rather than the schema so consumers can only
// read the fields the query actually selects.
export type FreeDeliveryOfferData = Extract<
  Extract<
    RankedToastFundedOfferQuery['offers']['rankedToastFundedOffers'],
    { __typename?: 'RankedToastFundedOffersResponse' }
  >['rankedOffers'][number],
  { __typename: 'RankedFreeDeliveryOffer' }
>

// A Toast-funded promotion must never appear on a restaurant's own storefront.
export const useFreeDeliveryChannelEnabled = () => {
  const { nv1ToastAppFreeDeliveryOrdering } = useFlags()
  const { toastProduct } = useRestaurant()

  return nv1ToastAppFreeDeliveryOrdering && isToastAppProduct(toastProduct)
}

export const isFreeDeliveryThresholdReached = (offer: FreeDeliveryOfferData) =>
  offer.percentCompleteInt >= 100

// amountNeeded, not minPurchaseAmount: the backend substitutes the restaurant's
// delivery minimum when that is higher.
export const useAmountNeededMessage = (offer?: FreeDeliveryOfferData) => {
  const formatMoney = useFormatMoney()

  if (!offer) {
    return ''
  }

  return t('free-delivery-progress.add-amount-to-get-free-delivery', {
    amount: formatMoney({
      amount: String(offer.amountNeeded),
      currency: offer.minPurchaseAmount.currency
    })
  })
}

export const useAppliedFreeDelivery = () => {
  const channelEnabled = useFreeDeliveryChannelEnabled()
  const { cart } = useCart()

  const appliedFundedOffers = channelEnabled
    ? cart?.appliedFundedOffers
    : undefined

  // The cart carries the offer at 0 until the guest earns it, so only a non-zero
  // amount means it is paying for delivery.
  const amount = getAppliedFreeDeliveryAmount(appliedFundedOffers)

  return { isApplied: amount > 0, amount }
}

// The Delivery line can only report as much of the credit as the fee itself, so
// the rest goes to Discounts rather than silently shrinking the total.
export const splitFreeDeliveryCredit = (
  creditAmount: number,
  deliveryFee: number
) => {
  const coveredByDeliveryFee = Math.min(creditAmount, deliveryFee)

  return {
    coveredByDeliveryFee,
    uncoveredCredit: creditAmount - coveredByDeliveryFee,
    discountedDeliveryFee: deliveryFee - coveredByDeliveryFee,
    // A restaurant that charges nothing for delivery has no price to cross out.
    isDeliveryFeeDiscounted: coveredByDeliveryFee > 0
  }
}

// With the gate closed nothing strikes the Delivery line, so free delivery stays
// in the Discounts row rather than disappearing from both.
export const useDiscountsRowFundedOffersTotal = () => {
  const channelEnabled = useFreeDeliveryChannelEnabled()
  const { cart } = useCart()

  return channelEnabled
    ? getNonFreeDeliveryFundedOffersTotal(cart?.appliedFundedOffers)
    : getAppliedFundedOffersTotal(cart?.appliedFundedOffers)
}

// The terms name the Boston pilot, so they only apply when the offer that ranked
// is that pilot rather than some later free delivery offer.
export const useBostonFreeDeliveryTerms = (
  offer: FreeDeliveryOfferData,
  skip?: boolean
) => {
  const channelEnabled = useFreeDeliveryChannelEnabled()
  const ooClient = useOOClient()

  const { data } = useBostonFreeDeliveryTfoQuery({
    // Nothing reads the terms until the guest opens the card.
    ssr: false,
    client: ooClient,
    skip: skip || !channelEnabled
  })

  const tfo = data?.fundedOffers?.bostonFreeDeliveryTFO

  return tfo?.active && tfo.guid === offer.guid ? tfo : undefined
}

export const useFreeDeliveryOffer = () => {
  const channelEnabled = useFreeDeliveryChannelEnabled()
  const { ooRestaurant, orderingDisabled } = useRestaurant()
  const { cartGuid, lastOrderSelectionChange, loadingCart } = useCart()
  const { isLoggedIn } = useCustomer()
  const ooClient = useOOClient()

  const restaurantGuid = ooRestaurant?.guid

  const skip = !channelEnabled || orderingDisabled || !restaurantGuid

  const { data, refetch } = useRankedToastFundedOfferQuery({
    // The offer is guest-specific and SSR output is cached in Redis.
    ssr: false,
    variables: { input: { restaurantGuid, cartGuid } },
    client: ooClient,
    fetchPolicy: 'cache-and-network',
    skip
  })

  // Neither query variable changes when the guest signs in or edits the cart, so
  // the server-scored offer has to be refetched by hand.
  const selectionChangedAt = lastOrderSelectionChange?.getTime()
  const wasLoggedIn = useRef(isLoggedIn)
  const lastSelectionChangedAt = useRef(selectionChangedAt)

  useEffect(() => {
    const nothingChanged =
      wasLoggedIn.current === isLoggedIn &&
      lastSelectionChangedAt.current === selectionChangedAt
    // Refetching mid-mutation would score the offer against the pre-edit cart.
    if (nothingChanged || loadingCart) {
      return
    }

    wasLoggedIn.current = isLoggedIn
    lastSelectionChangedAt.current = selectionChangedAt

    // refetch ignores skip, so the gates have to be re-checked here.
    if (!skip) {
      refetch()
    }
  }, [isLoggedIn, selectionChangedAt, loadingCart, refetch, skip])

  const offers = data?.offers?.rankedToastFundedOffers
  const response =
    offers?.__typename === 'RankedToastFundedOffersResponse'
      ? offers
      : undefined

  // rankedOffers is ordered most- to least-attainable.
  return response?.rankedOffers?.find(
    (offer): offer is FreeDeliveryOfferData =>
      offer.__typename === 'RankedFreeDeliveryOffer' && offer.active
  )
}
